import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    FormControl,
    Switch,
    FormControlLabel,
    FormGroup,
    Paper,
    InputLabel,
    InputBase,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { COLORS } from '../../../utils/Application_Constants';
import {
    getClinicianSmsPreference,
    putClinicianSmsPreference,
} from '../../../services/ProfileServices/profileService';

const useStyles = makeStyles((theme) => ({
    header: {
        color: COLORS.ST_TROPAZ,
        textAlign: 'left',
        fontSize: '20px',
        fontWeight: '700',
    },
    topHeader: {
        color: COLORS.DENIM,
        textAlign: 'left',
        fontSize: '24px',
        marginTop: '15px',
    },
    textLabel: {
        color: COLORS.RAVEN,
        fontWeight: '700',
        fontSize: '16px',
        textAlign: 'left',
        marginBottom: '20px',
        [theme.breakpoints.down('md')]: {
            margin: '20px 0 20px',
        },
    },
    inputLabel: {
        color: COLORS.RAVEN,
        fontWeight: '700',
        fontSize: '16px',
    },
    graphicContainer: {
        alignItems: 'stretch',
        justifyContent: 'center',
        paddingRight: '2rem',
    },
    graphic: {
        width: '100%',
    },
    progress: {
        height: '100vh',
    },
    paperRoot: {
        padding: '2rem',
    },
    textingContainer: {
        display: 'flex',
    },
    mobileContainer: {
        alignItems: 'center',
        marginTop: '-30px',
        marginLeft: '140px',
        [theme.breakpoints.down('md')]: {
            margin: '20px 0px 0px 0px',
        },
    },
    radioGroupLabel: {
        whitespace: 'wrap',
        textAlign: 'left',
    },
    dateGrid: {
        padding: '0px',
    },
    setWidthTo100: {
        width: '100%',
    },
    editLabel: {
        color: COLORS.LT_JOURNEY,
        fontSize: '16px',
        marginTop: '20px',
    },
    errorLabel: {
        color: 'red !important',
        fontSize: '16px',
        marginTop: '10px',
    },
    error: {
        border: '1px solid red !important', // focused
        borderWidth: '1px',
        borderRadius: 1,
    },
    errorColor: {
        color: 'red !important',
        cursor: 'pointer',
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'red !important',
            },
        },
    },
    cssLabel: {
        color: 'red',
        float: 'left',
        textAlign: 'left',
        fontSize: '16px',
    },
    dateGridWidth: {
        width: '100%',
        marginTop: '-16px',
    },
    typeAheadInput: {
        'label + &': {
            marginTop: theme.spacing(2),
        },
        borderRadius: 3,
        position: 'relative',
        backgroundColor: COLORS.WHITE,
        color: COLORS.RAVEN,
        border: `1px solid ${COLORS.WHITE_LILAC}`,
        fontSize: 16,
        width: '100%',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:focus': {
            boxShadow: COLORS.WHITE_LILAC,
            borderColor: COLORS.RAVEN,
        },
    },
    chipRoot: {
        backgroundColor: COLORS.ZIRCON,
        borderRadius: '3px',
    },
    chipLabel: {
        color: COLORS.RAVEN,
    },
    cssOutlinedInput: {
        height: 45,
        fontSize: '16px',
        color: COLORS.RAVEN,
        padding: '0px 0px',
        '&:not(hover):not($disabled):not($cssFocused):not($error) $notchedOutline': {
            borderColor: '#E7E7E7', // default
        },
        '&:hover:not($disabled):not($cssFocused):not($error) $notchedOutline': {
            borderColor: '#E7E7E7', // hovered
        },
        '&$cssFocused $notchedOutline': {
            borderColor: '#6D707E', // focused
            borderWidth: '1px',
            borderRadius: 1,
        },
    },
    disabledInput: {
        opacity: 0.6,
    },
    labelStyle: {
        color: COLORS.ST_TROPAZ,
        fontWeight: '700',
        fontSize: '16px',
        textAlign: 'left',
        textTransform: 'uppercase',
    },
    infoStyle: {
        color: COLORS.RAVEN,
        fontWeight: '500',
        fontSize: '16px',
        textAlign: 'left',
        textTransform: 'none',
    },
    infoAlert: {
        color: COLORS.LT_PEPPER,
        fontWeight: '500',
        fontSize: '16px',
        textAlign: 'left',
        textTransform: 'none',
        justifyContent: 'flex-start',
        marginTop: '-2.5rem',
        marginBottom: '1rem',
    },
    alertText: {
        color: COLORS.DENIM,
        fontWeight: '700',
        [theme.breakpoints.down('md')]: {
            display: 'flex',
        },
    },
    input: {
        fontFamily: 'museo-sans',
        borderRadius: 0,
        position: 'relative',
        backgroundColor: COLORS.WHITE,
        color: COLORS.LT_MIDNIGHT,
        border: `1px solid`,
        borderColor: COLORS.WHITE_LILAC,
        fontSize: '16px',
        width: '100%',
        padding: '12px',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:hover': {
            borderColor: COLORS.LT_JOURNEY,
        },
        '&:focus': {
            borderColor: COLORS.LT_JOURNEY,
        },
    },
}));

const SmsPreferences = ({
    setSaveHandler,
    flags,
    updateFlags,
    externalId,
    setPreferencePageError,
    t,
}) => {
    const classes = useStyles();

    const [smsPreferences, setSmsPreferences] = useState({
        sfId: null,
        clinicianId: null,
        mobilePhone: '',
        hasOptedOutOfSms: null,
        textOptOutOptInSource: '',
        textSourceSet: false,
        marketingTextOptInOutStatus: null,
        marketingTextOptInOutDate: null,
        marketingTextOptInOutSource: '',
        marketingTextSourceSet: false,
    });
    const [unsubscribeAll, setUnsubscribeAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [showOptOutAlert, setShowOptOutAlert] = useState(false);
    const hasInitialized = useRef(false);
    const ALL_UNSUBSCRIBED = {
        hasOptedOutOfSms: true,
        marketingTextOptInOutStatus: false,
    };

    const ALL_SUBSCRIBED = {
        hasOptedOutOfSms: false,
        marketingTextOptInOutStatus: true,
    };

    const normalizePhoneInput = (value) => {
        if (!value) return value;

        const onlyNums = value.replace(/[^\d]/g, '');

        if (onlyNums.length <= 3) {
            return onlyNums;
        }
        if (onlyNums.length <= 6) {
            return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3)}`;
        }
        return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
    };

    const shouldDisableSave = () => {
        const missingAnyFields =
            !smsPreferences.mobilePhone &&
            (smsPreferences.hasOptedOutOfSms === false ||
                smsPreferences.marketingTextOptInOutStatus === true);

        const updatedFlags = {
            ...flags,
            missingData: missingAnyFields,
        };
        updateFlags(updatedFlags);
    };

    const doSubmit = useCallback(async () => {
        const missingAnyFields =
            !smsPreferences.mobilePhone &&
            (smsPreferences.hasOptedOutOfSms === false ||
                smsPreferences.marketingTextOptInOutStatus === true);

        const updatedFlags = {
            ...flags,
            missingData: missingAnyFields,
        };

        updateFlags(updatedFlags);

        if (!missingAnyFields) {
            updateFlags({ ...updatedFlags, isPendingSave: true });

            try {
                await putClinicianSmsPreference(externalId, smsPreferences);
                updateFlags({
                    ...flags,
                    dataSaved: true,
                    isDirty: false,
                    isPendingSave: false,
                });
            } catch (e) {
                setPreferencePageError(e);
            }
        }
    }, [smsPreferences, externalId]);

    useEffect(() => {
        const fetchSmsPreference = async () => {
            try {
                const response = await getClinicianSmsPreference(externalId);
                if (response) {
                    const updatedPrefs = {
                        sfId: response.sfId,
                        clinicianId: response.clinicianId,
                        mobilePhone: response.mobilePhone,
                        hasOptedOutOfSms: response.hasOptedOutOfSms ?? false,
                        textOptOutOptInSource: response.textOptOutOptInSource,
                        textSourceSet: response.textSourceSet,
                        marketingTextOptInOutStatus: response.marketingTextOptInOutStatus ?? true,
                        marketingTextOptInOutDate: response.marketingTextOptInOutDate,
                        marketingTextOptInOutSource: response.marketingTextOptInOutSource,
                        marketingTextSourceSet: response.marketingTextSourceSet,
                    };
                    if (updatedPrefs.hasOptedOutOfSms === true) setShowOptOutAlert(true);
                    setSmsPreferences(updatedPrefs);
                }
            } catch (error) {
                setPreferencePageError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchSmsPreference();
        shouldDisableSave();
    }, [externalId]);

    useEffect(() => {
        if (smsPreferences && setSaveHandler) {
            setSaveHandler({ doSubmit }, () => {
                doSubmit();
            });
        }
    }, [smsPreferences, setSaveHandler, doSubmit]);

    const markFlagsDirty = () => {
        const updatedFlags = {
            ...flags,
            isDirty: true,
            dataSaved: false,
        };
        updateFlags(updatedFlags);
    };

    useEffect(() => {
        const { hasOptedOutOfSms, marketingTextOptInOutStatus } = smsPreferences;

        // Skip if still loading initial values
        if (hasOptedOutOfSms == null || marketingTextOptInOutStatus == null) return;

        const bothOptedOut = hasOptedOutOfSms && !marketingTextOptInOutStatus;

        if (bothOptedOut && !unsubscribeAll) {
            setUnsubscribeAll(true);
        } else if (!bothOptedOut && unsubscribeAll) {
            setUnsubscribeAll(false);
        }

        if (hasInitialized.current) {
            markFlagsDirty();
        } else {
            hasInitialized.current = true;
        }
    }, [
        smsPreferences.hasOptedOutOfSms,
        smsPreferences.marketingTextOptInOutStatus,
        unsubscribeAll,
    ]);

    const handleChange = (key) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

        if (key === 'hasOptedOutOfSms' && value === false) {
            setShowConfirmationDialog(true);
            return;
        }
        if (key === 'hasOptedOutOfSms' && value === true) {
            setShowOptOutAlert(false);
        }
        if (unsubscribeAll) {
            if (
                (key === 'hasOptedOutOfSms' && value === false) ||
                (key === 'marketingTextOptInOutStatus' && value === true)
            ) {
                setUnsubscribeAll(false);
            }
        }
        markFlagsDirty();
        setSmsPreferences((prev) => ({
            ...prev,
            [key]: key === 'hasOptedOutOfSms' ? !value : value,
        }));
    };

    const handleUnsubscribeToggle = (event) => {
        const {
            target: { checked },
        } = event;

        markFlagsDirty();
        setSmsPreferences((prev) => ({
            ...prev,
            ...(checked ? ALL_UNSUBSCRIBED : ALL_SUBSCRIBED),
        }));
        setUnsubscribeAll(checked);
        if (checked && smsPreferences.hasOptedOutOfSms === false) {
            setShowConfirmationDialog(checked);
        }
        if (!checked) {
            setShowOptOutAlert(false);
        }
    };

    const textFieldChangeHandlerProfilePhone = (event) => {
        const { value } = event.target;

        const normalizedPhone = normalizePhoneInput(value, smsPreferences.mobilePhone);
        markFlagsDirty();
        setSmsPreferences((prev) => ({
            ...prev,
            mobilePhone: normalizedPhone,
        }));
    };

    const handleConfirmationDialogClose = useCallback(() => {
        markFlagsDirty();
        setSmsPreferences((prev) => ({
            ...prev,
            hasOptedOutOfSms: false,
        }));
        setShowOptOutAlert(false);
        setShowConfirmationDialog(false);
        shouldDisableSave();
    }, [markFlagsDirty, shouldDisableSave]);

    const handleConfirmation = useCallback(() => {
        markFlagsDirty();
        setSmsPreferences((prev) => ({
            ...prev,
            hasOptedOutOfSms: true,
        }));
        setShowOptOutAlert(true);
        setShowConfirmationDialog(false);
        shouldDisableSave();
    }, [markFlagsDirty, shouldDisableSave]);

    const renderConfirmationDialog = () => {
        if (!showConfirmationDialog) return null;
        return (
            <Dialog
                open={showConfirmationDialog}
                onClose={handleConfirmationDialogClose}
                aria-labelledby="confirmation-dialog-title"
                aria-describedby="confirmation-dialog-description"
                className={classes.alertText}
            >
                <DialogTitle id="confirmation-dialog-title">Text Opt Out Alert</DialogTitle>
                <DialogContent dividers>
                    You&apos;ve selected that you&apos;d like to opt-out of text messages. This
                    action also opts you out of receiving texts (including responses to your
                    inquiries) from your LocumTenens.com recruiter in any capacity. Do you still
                    want to opt-out?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmationDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmation} color="primary" autoFocus>
                        Yes, Opt-Out
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper classes={{ root: classes.paperRoot }}>
            <Typography variant="h3" gutterBottom className={classes.header}>
                SMS communication preferences
            </Typography>

            <Box>
                <FormControl>
                    <InputLabel
                        shrink
                        htmlFor="Mobile Number"
                        classes={{ root: classes.inputLabel }}
                    >
                        Mobile Number
                    </InputLabel>
                    <InputBase
                        id="MobileNumber"
                        classes={{
                            input: classes.input,
                        }}
                        onChange={textFieldChangeHandlerProfilePhone}
                        name="mobileNumber"
                        placeholder="(xxx) xxx-xxxx"
                        value={normalizePhoneInput(smsPreferences.mobilePhone)}
                    />
                </FormControl>
            </Box>

            <FormGroup sx={{ mt: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography className={classes.textLabel}>
                            One-to-one texts with your LocumTenens.com Account Executive
                        </Typography>
                        <Typography className={classes.infoStyle}>
                            By toggling to &quot;yes/no&quot;, I give my expressed consent to
                            receive text messages from my LT.com Account Executive at my number
                            provided above. I understand my consent is not a condition of doing
                            business with LocumTenens.com.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!smsPreferences.hasOptedOutOfSms}
                                onChange={handleChange('hasOptedOutOfSms')}
                            />
                        }
                        label=""
                    />
                </Box>
                {showOptOutAlert === true ? (
                    <Box container className="cr-mt-4">
                        <Box item xs={12}>
                            <div className={classes.infoAlert}>
                                {t('profile:COMMUNICATION.OPTOUT_ALERT')}
                            </div>
                        </Box>
                    </Box>
                ) : null}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography className={classes.textLabel}>
                            LocumTenens.com Marketing Texts
                        </Typography>
                        <Typography className={classes.infoStyle}>
                            By toggling &quot;yes/no&quot;, I opt-in to receive messages from
                            LocumTenens.com regarding open assignments, upcoming conventions/events,
                            product releases, and general information about their services.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={smsPreferences.marketingTextOptInOutStatus ?? true}
                                onChange={handleChange('marketingTextOptInOutStatus')}
                            />
                        }
                        label=""
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <Box>
                        <Typography className={classes.textLabel}>
                            Unsubscribe from all SMS communication
                        </Typography>
                        <Typography className={classes.infoStyle}>
                            Remove me from all LocumTenens.com SMS communication.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch checked={unsubscribeAll} onChange={handleUnsubscribeToggle} />
                        }
                        label=""
                    />
                </Box>
                {renderConfirmationDialog()}
            </FormGroup>
        </Paper>
    );
};

export default SmsPreferences;
